import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  ArrowRight, 
  FileText, 
  CheckCircle, 
  AlertTriangle, 
  Calendar, 
  Clock,
  FileCheck,
  Target,
  Zap,
  XCircle,
  Shield, 
  Info, 
  HelpCircle
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { CFELogo } from "@/components/CFELogo";

export default function Formulaire1447CSD() {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Formulaire 1447-C-SD auto-entrepreneur : comment le remplir et le générer facilement";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute("content", "Guide complet du formulaire 1447-C-SD pour auto-entrepreneur. Découvrez comment remplir et générer votre déclaration initiale de CFE étape par étape.");
    } else {
      const meta = document.createElement("meta");
      meta.name = "description";
      meta.content = "Guide complet du formulaire 1447-C-SD pour auto-entrepreneur. Découvrez comment remplir et générer votre déclaration initiale de CFE étape par étape.";
      document.head.appendChild(meta);
    }

    return () => {
      document.title = "CFE Auto-Entrepreneur | Déclaration CFE simplifiée - Formulaire 1447-C-SD";
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Navigation */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 sm:h-16">
            <a href="/" className="flex items-center gap-2">
              <CFELogo size={28} />
              <span className="font-bold text-lg text-[#0F172A]">CFE Auto-Entrepreneur</span>
            </a>
            <Button 
              onClick={() => navigate('/')}
              className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white"
            >
              <a href="/#formulaire" className="flex items-center text-white no-underline">
                Commencer
              </a>
            </Button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <article className="prose prose-slate max-w-none">
          
          {/* Breadcrumb */}
          <nav className="text-sm text-[#64748B] mb-8" aria-label="Breadcrumb">
            <a href="/" className="hover:text-[#2563EB]">Accueil</a>
            <span className="mx-2">/</span>
            <span className="text-[#0F172A]">Formulaire 1447-C-SD auto-entrepreneur</span>
          </nav>

          {/* H1 - Main title */}
          <h1 className="text-3xl sm:text-4xl font-bold text-[#0F172A] mb-6 leading-tight">
            Formulaire 1447-C-SD auto-entrepreneur : comment le remplir et le générer facilement
          </h1>

          {/* Introduction pédagogique */}
          <div className="bg-white rounded-xl border border-slate-200 p-6 mb-8">
            <p className="text-lg text-[#475569] leading-relaxed mb-4">
              Le <strong>formulaire 1447-C-SD</strong> est le document officiel utilisé pour effectuer la déclaration initiale de Cotisation Foncière des Entreprises (CFE). 
              Tout <strong>auto-entrepreneur</strong> ou <strong>micro-entrepreneur</strong> doit le remplir lors de la création de son activité.
            </p>
            <p className="text-[#475569] leading-relaxed mb-4">
              Ce formulaire administratif est souvent source de confusion : terminologie technique, nombreuses rubriques à compléter, 
              et risque d&apos;erreurs qui peuvent entraîner des complications avec l&apos;administration fiscale.
            </p>
            <p className="text-[#475569] leading-relaxed m-0">
              Ce guide vous explique clairement <strong>à quoi sert le formulaire 1447-C-SD</strong>, 
              <strong> comment le remplir correctement</strong>, et comment le <strong>générer automatiquement</strong> grâce à notre assistant guidé.
            </p>
          </div>

          {/* Disclaimer Box */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-8 flex items-start gap-3">
            <Info className="h-5 w-5 text-[#2563EB] mt-0.5 flex-shrink-0" />
            <div className="text-sm text-[#475569]">
              <p className="font-medium text-[#0F172A] mb-1">Information importante</p>
              <p className="m-0">
                Ce service n&apos;est pas affilié à l&apos;administration fiscale française. 
                Le document généré doit être vérifié par l&apos;utilisateur avant envoi au Service des Impôts des Entreprises.
              </p>
            </div>
          </div>

          {/* Table of contents */}
          <Card className="mb-10 bg-slate-50 border-slate-200">
            <CardContent className="p-6">
              <p className="font-semibold text-[#0F172A] mb-4">Sommaire</p>
              <ul className="space-y-2 text-[#475569] list-none pl-0 m-0">
                <li className="flex items-center gap-2">
                  <span className="text-[#2563EB]">1.</span>
                  <a href="#a-quoi-sert" className="hover:text-[#2563EB] no-underline">À quoi sert le formulaire 1447-C-SD ?</a>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-[#2563EB]">2.</span>
                  <a href="#quand-remplir" className="hover:text-[#2563EB] no-underline">Quand faut-il remplir le formulaire 1447-C-SD ?</a>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-[#2563EB]">3.</span>
                  <a href="#comment-remplir" className="hover:text-[#2563EB] no-underline">Comment remplir le formulaire 1447-C-SD étape par étape</a>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-[#2563EB]">4.</span>
                  <a href="#erreurs-frequentes" className="hover:text-[#2563EB] no-underline">Erreurs fréquentes à éviter</a>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-[#2563EB]">5.</span>
                  <a href="#generer-formulaire" className="hover:text-[#2563EB] no-underline">Générer automatiquement le formulaire 1447-C-SD</a>
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Section 1: À quoi sert le formulaire 1447-C-SD */}
          <section id="a-quoi-sert" className="mb-12 scroll-mt-20">
            <h2 className="text-2xl font-bold text-[#0F172A] mb-6 flex items-center gap-3">
              <Target className="h-6 w-6 text-[#2563EB]" />
              À quoi sert le formulaire 1447-C-SD ?
            </h2>

            <p className="text-[#475569] mb-4">
              Le <strong>formulaire 1447-C-SD</strong>, également appelé &quot;Déclaration initiale de Cotisation Foncière des Entreprises&quot;, 
              est un document fiscal officiel qui permet de déclarer votre établissement professionnel auprès de l&apos;administration fiscale.
            </p>

            <h3 className="text-lg font-semibold text-[#0F172A] mb-3">Déclaration initiale de CFE</h3>
            <p className="text-[#475569] mb-4">
              Ce formulaire constitue la première démarche obligatoire pour tout entrepreneur vis-à-vis de la <strong>Cotisation Foncière des Entreprises (CFE)</strong>. 
              La CFE est un impôt local dû par toutes les personnes physiques ou morales qui exercent une activité professionnelle non salariée.
            </p>

            <h3 className="text-lg font-semibold text-[#0F172A] mb-3">Lien avec la Cotisation Foncière des Entreprises</h3>
            <p className="text-[#475569] mb-4">
              La CFE remplace l&apos;ancienne taxe professionnelle. Elle est calculée sur la base de la <strong>valeur locative des biens immobiliers</strong> 
              utilisés pour l&apos;exercice de votre activité. Pour les auto-entrepreneurs travaillant à domicile ou sans local fixe, 
              une <strong>cotisation minimum</strong> est appliquée, dont le montant varie selon les communes.
            </p>

            <h3 className="text-lg font-semibold text-[#0F172A] mb-3">Cas concernés par le formulaire 1447-C-SD</h3>
            <p className="text-[#475569] mb-4">
              Le formulaire 1447-C-SD doit être rempli dans les situations suivantes :
            </p>
            <ul className="space-y-2 text-[#475569] mb-6">
              <li className="flex items-start gap-2">
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                <span><strong>Création d&apos;établissement</strong> : lors du démarrage de votre auto-entreprise</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                <span><strong>Reprise d&apos;établissement</strong> : en cas d&apos;acquisition ou de transfert d&apos;une activité existante</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                <span><strong>Changement d&apos;exploitant</strong> : si vous reprenez l&apos;activité d&apos;un autre professionnel</span>
              </li>
            </ul>

            {/* Bon à savoir */}
            <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-lg">
              <p className="font-medium text-[#0F172A] mb-1">Bon à savoir</p>
              <p className="text-[#475569] text-sm m-0">
                Les auto-entrepreneurs sont exonérés de CFE la première année civile d&apos;activité. 
                Cependant, le formulaire 1447-C-SD doit tout de même être rempli et envoyé pour bénéficier de cette exonération.
              </p>
            </div>
          </section>

          {/* Section 2: Quand faut-il remplir le formulaire 1447-C-SD */}
          <section id="quand-remplir" className="mb-12 scroll-mt-20">
            <h2 className="text-2xl font-bold text-[#0F172A] mb-6 flex items-center gap-3">
              <Calendar className="h-6 w-6 text-[#2563EB]" />
              Quand faut-il remplir le formulaire 1447-C-SD ?
            </h2>

            <h3 className="text-lg font-semibold text-[#0F172A] mb-3">Délais légaux</h3>
            <p className="text-[#475569] mb-4">
              Le formulaire 1447-C-SD doit être envoyé au Service des Impôts des Entreprises (SIE) 
              <strong> avant le 31 décembre de l&apos;année de création</strong> de votre auto-entreprise.
            </p>

            <div className="bg-white rounded-lg border border-slate-200 p-5 mb-6">
              <p className="font-medium text-[#0F172A] mb-2">Exemple concret</p>
              <p className="text-[#475569] text-sm m-0">
                Si vous créez votre auto-entreprise en avril 2025, vous devez envoyer le formulaire 1447-C-SD 
                au plus tard le 31 décembre 2025.
              </p>
            </div>

            <h3 className="text-lg font-semibold text-[#0F172A] mb-3">Première année d&apos;activité</h3>
            <p className="text-[#475569] mb-4">
              Durant votre première année civile d&apos;activité, vous bénéficiez d&apos;une <strong>exonération totale de CFE</strong>. 
              La déclaration initiale permet aux services fiscaux de vous enregistrer et de préparer l&apos;imposition à partir de la deuxième année.
            </p>

            <h3 className="text-lg font-semibold text-[#0F172A] mb-3">Cas particuliers</h3>
            <ul className="space-y-3 text-[#475569] mb-4">
              <li className="flex items-start gap-2">
                <Clock className="h-5 w-5 text-[#2563EB] mt-0.5 flex-shrink-0" />
                <span><strong>Activité créée en fin d&apos;année</strong> : même si vous créez votre activité en décembre, vous devez envoyer le formulaire avant le 31 décembre de cette même année</span>
              </li>
              <li className="flex items-start gap-2">
                <Clock className="h-5 w-5 text-[#2563EB] mt-0.5 flex-shrink-0" />
                <span><strong>Changement d&apos;adresse</strong> : si vous déménagez votre lieu d&apos;exercice, un nouveau formulaire peut être nécessaire</span>
              </li>
              <li className="flex items-start gap-2">
                <Clock className="h-5 w-5 text-[#2563EB] mt-0.5 flex-shrink-0" />
                <span><strong>Modification de surface</strong> : en cas de changement significatif des locaux utilisés</span>
              </li>
            </ul>

            {/* Important box */}
            <div className="bg-blue-50 border-l-4 border-[#2563EB] p-4 rounded-r-lg">
              <p className="font-medium text-[#0F172A] mb-1 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-[#2563EB]" />
                Important
              </p>
              <p className="text-[#475569] text-sm m-0">
                Le formulaire 1447-C-SD doit être envoyé au <strong>Service des Impôts des Entreprises (SIE)</strong> dont dépend 
                l&apos;adresse de votre établissement, ou déposé via votre espace professionnel sur impots.gouv.fr.
              </p>
            </div>
          </section>

          {/* Section 3: Comment remplir le formulaire étape par étape */}
          <section id="comment-remplir" className="mb-12 scroll-mt-20">
            <h2 className="text-2xl font-bold text-[#0F172A] mb-6 flex items-center gap-3">
              <FileText className="h-6 w-6 text-[#2563EB]" />
              Comment remplir le formulaire 1447-C-SD étape par étape
            </h2>

            <p className="text-[#475569] mb-6">
              Le formulaire 1447-C-SD est structuré en plusieurs sections. Voici comment compléter chaque partie :
            </p>

            {/* Section A1 */}
            <div className="bg-white rounded-lg border border-slate-200 p-5 mb-4">
              <div className="flex items-center gap-2 mb-3">
                <span className="bg-[#2563EB] text-white text-sm font-bold px-2 py-1 rounded">A1</span>
                <h3 className="text-lg font-semibold text-[#0F172A] m-0">Identification de l&apos;entreprise</h3>
              </div>
              <p className="text-[#475569] mb-3">
                Cette section regroupe vos informations d&apos;identité et d&apos;immatriculation :
              </p>
              <ul className="space-y-1 text-[#475569] text-sm">
                <li>• <strong>Dénomination</strong> : votre nom et prénom (ou raison sociale)</li>
                <li>• <strong>Numéro SIRET</strong> : les 14 chiffres de votre numéro SIRET</li>
                <li>• <strong>Code NACE/APE</strong> : le code à 4 chiffres et 1 lettre qui définit votre activité</li>
                <li>• <strong>Adresse de l&apos;établissement</strong> : l&apos;adresse où vous exercez votre activité</li>
                <li>• <strong>Activité principale</strong> : description claire de votre activité professionnelle</li>
              </ul>
            </div>

            {/* Section A2 */}
            <div className="bg-white rounded-lg border border-slate-200 p-5 mb-4">
              <div className="flex items-center gap-2 mb-3">
                <span className="bg-[#2563EB] text-white text-sm font-bold px-2 py-1 rounded">A2</span>
                <h3 className="text-lg font-semibold text-[#0F172A] m-0">Activité exercée à domicile ou chez les clients</h3>
              </div>
              <p className="text-[#475569] mb-3">
                Si vous travaillez depuis votre domicile ou directement chez vos clients (sans local professionnel dédié) :
              </p>
              <ul className="space-y-1 text-[#475569] text-sm">
                <li>• Cochez la case correspondante</li>
                <li>• Indiquez la <strong>surface en m²</strong> dédiée à votre activité professionnelle</li>
                <li>• Cette information sert au calcul de la base d&apos;imposition</li>
              </ul>
            </div>

            {/* Section A3 */}
            <div className="bg-white rounded-lg border border-slate-200 p-5 mb-4">
              <div className="flex items-center gap-2 mb-3">
                <span className="bg-[#2563EB] text-white text-sm font-bold px-2 py-1 rounded">A3</span>
                <h3 className="text-lg font-semibold text-[#0F172A] m-0">Origine de l&apos;établissement</h3>
              </div>
              <p className="text-[#475569] mb-3">
                Précisez comment vous avez obtenu cet établissement :
              </p>
              <ul className="space-y-1 text-[#475569] text-sm">
                <li>• <strong>Création</strong> : nouvelle activité que vous démarrez</li>
                <li>• <strong>Achat</strong> : reprise d&apos;une activité existante par acquisition</li>
                <li>• <strong>Apport</strong> : transfert d&apos;une activité dans une nouvelle structure</li>
                <li>• <strong>Transfert</strong> : déménagement d&apos;une activité existante</li>
                <li>• Indiquez la <strong>date de création ou de début d&apos;activité</strong></li>
              </ul>
            </div>

            {/* Section B1/B2 */}
            <div className="bg-white rounded-lg border border-slate-200 p-5 mb-4">
              <div className="flex items-center gap-2 mb-3">
                <span className="bg-[#2563EB] text-white text-sm font-bold px-2 py-1 rounded">B1/B2</span>
                <h3 className="text-lg font-semibold text-[#0F172A] m-0">Données économiques</h3>
              </div>
              <p className="text-[#475569] mb-3">
                Ces sections concernent les informations économiques de votre activité :
              </p>
              <ul className="space-y-1 text-[#475569] text-sm">
                <li>• <strong>Chiffre d&apos;affaires prévisionnel</strong> (ou réalisé si l&apos;activité existe déjà)</li>
                <li>• <strong>Nombre de salariés</strong> (0 pour la plupart des auto-entrepreneurs)</li>
                <li>• <strong>Véhicules utilisés</strong> pour l&apos;activité professionnelle (le cas échéant)</li>
              </ul>
            </div>

            {/* Section C */}
            <div className="bg-white rounded-lg border border-slate-200 p-5 mb-4">
              <div className="flex items-center gap-2 mb-3">
                <span className="bg-[#2563EB] text-white text-sm font-bold px-2 py-1 rounded">C</span>
                <h3 className="text-lg font-semibold text-[#0F172A] m-0">Biens et locaux</h3>
              </div>
              <p className="text-[#475569] mb-3">
                Décrivez les locaux que vous utilisez pour votre activité :
              </p>
              <ul className="space-y-1 text-[#475569] text-sm">
                <li>• <strong>Adresse du local</strong> professionnel (si différente de l&apos;établissement)</li>
                <li>• <strong>Surface totale</strong> utilisée pour l&apos;activité</li>
                <li>• <strong>Type de local</strong> : bureau, atelier, entrepôt, etc.</li>
                <li>• <strong>Statut d&apos;occupation</strong> : propriétaire, locataire, domicile personnel</li>
              </ul>
            </div>

            {/* Section D */}
            <div className="bg-white rounded-lg border border-slate-200 p-5 mb-6">
              <div className="flex items-center gap-2 mb-3">
                <span className="bg-[#2563EB] text-white text-sm font-bold px-2 py-1 rounded">D</span>
                <h3 className="text-lg font-semibold text-[#0F172A] m-0">Exonérations</h3>
              </div>
              <p className="text-[#475569] mb-3">
                Vérifiez si vous pouvez bénéficier d&apos;exonérations de CFE :
              </p>
              <ul className="space-y-1 text-[#475569] text-sm">
                <li>• <strong>Exonération de début d&apos;activité</strong> (automatique la première année)</li>
                <li>• <strong>Zones de revitalisation rurale (ZRR)</strong></li>
                <li>• <strong>Zones franches urbaines (ZFU)</strong></li>
                <li>• <strong>Bassins d&apos;emploi à redynamiser (BER)</strong></li>
                <li>• <strong>Activités artisanales</strong> sous certaines conditions</li>
              </ul>
            </div>

            {/* Tip box */}
            <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-r-lg">
              <p className="font-medium text-[#0F172A] mb-1 flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-600" />
                Conseil
              </p>
              <p className="text-[#475569] text-sm m-0">
                Si certaines informations ne s&apos;appliquent pas à votre situation (par exemple, vous n&apos;avez pas de local professionnel distinct), 
                laissez simplement les champs correspondants vides ou cochez &quot;sans objet&quot; si cette option est proposée.
              </p>
            </div>
          </section>

          {/* Section 4: Erreurs fréquentes à éviter */}
          <section id="erreurs-frequentes" className="mb-12 scroll-mt-20">
            <h2 className="text-2xl font-bold text-[#0F172A] mb-6 flex items-center gap-3">
              <AlertTriangle className="h-6 w-6 text-[#2563EB]" />
              Erreurs fréquentes à éviter
            </h2>

            <p className="text-[#475569] mb-6">
              Pour éviter les complications avec l&apos;administration fiscale, voici les erreurs les plus courantes lors du remplissage du formulaire 1447-C-SD :
            </p>

            <div className="space-y-4 mb-6">
              <div className="bg-white rounded-lg border border-red-200 p-5">
                <div className="flex items-start gap-3">
                  <XCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-[#0F172A] mb-1">Numéro SIRET incorrect</h3>
                    <p className="text-[#475569] text-sm m-0">
                      Le numéro SIRET doit comporter exactement 14 chiffres. Vérifiez-le sur votre extrait Kbis ou sur le site de l&apos;INSEE. 
                      Une erreur de chiffre peut entraîner un rejet du formulaire ou un mauvais rattachement à votre dossier fiscal.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg border border-red-200 p-5">
                <div className="flex items-start gap-3">
                  <XCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-[#0F172A] mb-1">Mauvaise date de création</h3>
                    <p className="text-[#475569] text-sm m-0">
                      La date de création doit correspondre à la date officielle d&apos;immatriculation de votre auto-entreprise. 
                      Une date erronée peut affecter le calcul de votre première année d&apos;exonération.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg border border-red-200 p-5">
                <div className="flex items-start gap-3">
                  <XCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-[#0F172A] mb-1">Oubli des exonérations</h3>
                    <p className="text-[#475569] text-sm m-0">
                      De nombreux auto-entrepreneurs oublient de cocher les cases d&apos;exonération auxquelles ils ont droit. 
                      Vérifiez notamment si vous êtes en zone ZRR, ZFU, ou si vous exercez une activité artisanale éligible.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg border border-red-200 p-5">
                <div className="flex items-start gap-3">
                  <XCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-[#0F172A] mb-1">Mauvaise surface déclarée</h3>
                    <p className="text-[#475569] text-sm m-0">
                      Si vous travaillez à domicile, déclarez uniquement la surface réellement dédiée à votre activité professionnelle. 
                      Une surface surévaluée augmentera inutilement votre base d&apos;imposition CFE.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg border border-red-200 p-5">
                <div className="flex items-start gap-3">
                  <XCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-[#0F172A] mb-1">Code APE/NACE erroné</h3>
                    <p className="text-[#475569] text-sm m-0">
                      Le code APE doit correspondre à votre activité principale. 
                      Un code incorrect peut entraîner une mauvaise classification et potentiellement affecter certaines exonérations.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Tip */}
            <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-lg">
              <p className="font-medium text-[#0F172A] mb-1">Comment éviter ces erreurs ?</p>
              <p className="text-[#475569] text-sm m-0">
                Utilisez notre assistant guidé qui vous pose les bonnes questions et vérifie automatiquement 
                la cohérence de vos informations avant de générer le formulaire.
              </p>
            </div>
          </section>

          {/* Section 5: Générer automatiquement le formulaire */}
          <section id="generer-formulaire" className="mb-12 scroll-mt-20">
            <h2 className="text-2xl font-bold text-[#0F172A] mb-6 flex items-center gap-3">
              <Zap className="h-6 w-6 text-[#2563EB]" />
              Générer automatiquement le formulaire 1447-C-SD
            </h2>

            <p className="text-[#475569] mb-6">
              Plutôt que de remplir manuellement le formulaire officiel (souvent complexe et source d&apos;erreurs), 
              utilisez notre <strong>assistant guidé</strong> pour générer votre déclaration 1447-C-SD en quelques minutes.
            </p>

            <div className="bg-gradient-to-br from-blue-50 to-slate-50 rounded-xl border border-slate-200 p-6 mb-6">
              <h3 className="text-lg font-semibold text-[#0F172A] mb-4">Pourquoi utiliser notre assistant ?</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <div className="bg-[#2563EB] rounded-full p-1.5 flex-shrink-0">
                    <Clock className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-[#0F172A] mb-1">Gain de temps</p>
                    <p className="text-[#475569] text-sm m-0">Complétez votre déclaration en 5 minutes au lieu de 30</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="bg-[#2563EB] rounded-full p-1.5 flex-shrink-0">
                    <Shield className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-[#0F172A] mb-1">Conforme au formulaire officiel</p>
                    <p className="text-[#475569] text-sm m-0">Structure identique au 1447-C-SD de l&apos;administration</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="bg-[#2563EB] rounded-full p-1.5 flex-shrink-0">
                    <HelpCircle className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-[#0F172A] mb-1">Questions guidées</p>
                    <p className="text-[#475569] text-sm m-0">Chaque champ est expliqué simplement</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="bg-[#2563EB] rounded-full p-1.5 flex-shrink-0">
                    <FileCheck className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-[#0F172A] mb-1">PDF prêt à envoyer</p>
                    <p className="text-[#475569] text-sm m-0">Document professionnel à télécharger immédiatement</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-slate-200 p-5 mb-6">
              <h3 className="font-semibold text-[#0F172A] mb-3">Comment ça fonctionne ?</h3>
              <ol className="space-y-2 text-[#475569] list-decimal pl-5 m-0">
                <li>Répondez aux questions de l&apos;assistant (identité, activité, locaux, etc.)</li>
                <li>Vérifiez l&apos;aperçu de votre déclaration</li>
                <li>Téléchargez votre formulaire 1447-C-SD au format PDF</li>
                <li>Envoyez le document à votre Service des Impôts des Entreprises</li>
              </ol>
            </div>

            {/* Reassurance */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-6 flex items-start gap-3">
              <Shield className="h-5 w-5 text-[#64748B] mt-0.5 flex-shrink-0" />
              <div className="text-sm text-[#475569]">
                <p className="font-medium text-[#0F172A] mb-1">Mentions importantes</p>
                <ul className="space-y-1 m-0 pl-0 list-none">
                  <li>• Ce service n&apos;est pas affilié à l&apos;administration fiscale française</li>
                  <li>• Le document généré doit être vérifié par vos soins avant envoi</li>
                  <li>• Ce service ne constitue pas un conseil fiscal personnalisé</li>
                </ul>
              </div>
            </div>

            {/* CTA */}
            <div className="text-center">
              <a href="/#formulaire">
                <Button 
                  size="lg"
                  className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white h-12 sm:h-14 px-6 sm:px-8 text-base sm:text-lg"
                >
                  Commencer ma déclaration CFE
                  <ArrowRight className="ml-2 h-5 w-5 flex-shrink-0" />
                </Button>
              </a>
            </div>
          </section>

          {/* FAQ Section for Rich Snippets */}
          <section id="faq" className="mb-12 scroll-mt-20">
            <h2 className="text-2xl font-bold text-[#0F172A] mb-6 flex items-center gap-3">
              <HelpCircle className="h-6 w-6 text-[#2563EB]" />
              Questions fréquentes sur le formulaire 1447-C-SD
            </h2>

            <div className="space-y-4" itemScope itemType="https://schema.org/FAQPage">
              
              <div className="bg-white rounded-lg border border-slate-200 p-5" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                <h3 className="font-semibold text-[#0F172A] mb-2" itemProp="name">
                  Où trouver le formulaire 1447-C-SD officiel ?
                </h3>
                <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                  <p className="text-[#475569] m-0" itemProp="text">
                    Le formulaire 1447-C-SD officiel est disponible sur le site impots.gouv.fr dans la rubrique &quot;Formulaires&quot;. 
                    Vous pouvez également utiliser notre assistant pour générer un document conforme à envoyer à votre SIE.
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-lg border border-slate-200 p-5" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                <h3 className="font-semibold text-[#0F172A] mb-2" itemProp="name">
                  Quelle est la différence entre le 1447-C-SD et le 1447-M-SD ?
                </h3>
                <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                  <p className="text-[#475569] m-0" itemProp="text">
                    Le formulaire 1447-C-SD est utilisé pour la déclaration initiale lors de la création ou reprise d&apos;établissement. 
                    Le formulaire 1447-M-SD sert aux déclarations modificatives (changement de surface, d&apos;activité, etc.) 
                    pour les établissements déjà enregistrés.
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-lg border border-slate-200 p-5" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                <h3 className="font-semibold text-[#0F172A] mb-2" itemProp="name">
                  Que se passe-t-il si je ne remplis pas le formulaire 1447-C-SD ?
                </h3>
                <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                  <p className="text-[#475569] m-0" itemProp="text">
                    L&apos;absence de déclaration initiale peut entraîner une taxation d&apos;office par l&apos;administration fiscale, 
                    basée sur des estimations qui peuvent être défavorables. Vous risquez également des pénalités de retard. 
                    Il est donc important de remplir et envoyer ce formulaire dans les délais.
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-lg border border-slate-200 p-5" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                <h3 className="font-semibold text-[#0F172A] mb-2" itemProp="name">
                  Peut-on remplir le formulaire 1447-C-SD en ligne ?
                </h3>
                <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                  <p className="text-[#475569] m-0" itemProp="text">
                    Vous pouvez déposer votre déclaration CFE via votre espace professionnel sur impots.gouv.fr. 
                    Alternativement, vous pouvez utiliser notre assistant pour générer un PDF conforme, 
                    puis l&apos;envoyer par courrier ou le téléverser sur votre espace en ligne.
                  </p>
                </div>
              </div>

            </div>
          </section>

          {/* CTA Final */}
          <section className="bg-white rounded-xl border border-slate-200 p-6 sm:p-8 text-center">
            <h2 className="text-xl font-bold text-[#0F172A] mb-3">
              Prêt à générer votre formulaire 1447-C-SD ?
            </h2>
            <p className="text-[#475569] mb-6">
              Notre assistant guidé vous accompagne étape par étape pour créer votre déclaration CFE en quelques minutes.
            </p>
            <a href="/#formulaire">
              <Button 
                size="lg"
                className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white h-12 sm:h-14 px-6 sm:px-8 text-base sm:text-lg w-full sm:w-auto"
              >
                Commencer ma déclaration CFE
                <ArrowRight className="ml-2 h-5 w-5 flex-shrink-0" />
              </Button>
            </a>
          </section>

        </article>
      </main>

      {/* Footer */}
      <footer className="bg-[#0F172A] text-white py-12 px-4 sm:px-6 lg:px-8 mt-16">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <CFELogo size={28} />
                <span className="font-bold text-lg">CFE Auto-Entrepreneur</span>
              </div>
              <p className="text-slate-400 text-sm">
                Service de génération de déclaration CFE simplifié pour les auto-entrepreneurs français.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Liens utiles</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="/#formulaire" className="text-slate-400 hover:text-[#2563EB]">Remplir ma déclaration</a></li>
                <li><a href="/#faq" className="text-slate-400 hover:text-[#2563EB]">FAQ</a></li>
                <li><a href="/comment-remplir-cfe-auto-entrepreneur" className="text-slate-400 hover:text-[#2563EB]">Guide CFE auto-entrepreneur</a></li>
                <li><a href="/mentions-legales" className="text-slate-400 hover:text-[#2563EB]">Mentions légales</a></li>
                <li><a href="/cgv" className="text-slate-400 hover:text-[#2563EB]">CGV</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Informations importantes</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li>Ce service n&apos;est pas affilié à l&apos;administration fiscale française.</li>
                <li>Ce service ne constitue pas un conseil fiscal.</li>
                <li>Le document généré doit être vérifié par l&apos;utilisateur avant envoi.</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <p className="text-slate-400 text-sm mb-2">Une question ? Contactez-nous :</p>
              <a 
                href="mailto:contact@cfe-autoentrepreneur.fr" 
                className="text-sm text-slate-400 hover:text-[#2563EB] transition-colors"
              >
                contact@cfe-autoentrepreneur.fr
              </a>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-8 pt-8 text-center text-sm text-slate-400">
            <p>© {new Date().getFullYear()} CFE Auto-Entrepreneur. Tous droits réservés.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
